import { useState, useEffect, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { sourcesApi, scansApi, scannersApi, inventoryApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import { useDiscoveryStore } from '@/stores/discoveryStore'
import { ModalityBadge, StatusBadge } from '@/components/common/StatusBadge'
import { isLiveNetworkSource } from '@/lib/live-network'
import ScannerOptionsEditor from '@/components/common/ScannerOptionsEditor'
import { scannerOptionsFor } from '@/lib/scanner-options'
import { describeScanFrequency, isUploadDrivenType } from '@/lib/scan-schedule'
import type { ScannerInfo, Modality, Source, SourceType, CreateSourceRequest, UpdateSourceRequest, Inventory } from '@/api/types'

const MODALITIES: Modality[] = ['source', 'binary', 'runtime', 'network', 'infra', 'manual']

const SOURCE_TYPES_BY_MODALITY: Record<Modality, SourceType[]> = {
  source:  ['git', 'iac', 'config-mgmt', 'sast-tool', 'sca-tool', 'source-upload'],
  binary:  ['binary-upload'],
  runtime: ['runtime', 'container'],
  network: ['network', 'api', 'network-upload'],
  infra:   ['kms-aws', 'kms-azure', 'kms-gcp', 'kms-vault', 'on-prem', 'hsm', 'ca', 'cspm-tool'],
  manual:  ['source-upload', 'manual'],
}

/** Human-readable labels for source types */
const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  'git':        'Git Repository',
  'iac':        'IaC (Terraform / CloudFormation / Pulumi)',
  'config-mgmt':'Config Mgmt (Ansible / Chef / Puppet)',
  'sast-tool':  'SAST Tool (Checkmarx, SonarQube, Semgrep)',
  'sca-tool':   'SCA Tool (Snyk, Black Duck, FOSSA)',
  'runtime':    'Runtime / Process Inspection',
  'container':  'Container Image Registry',
  'network':    'Network / TLS Traffic',
  'api':        'API Scanning (JWKs, Signatures)',
  'kms-aws':    'AWS KMS / ACM / Secrets Manager',
  'kms-azure':  'Azure Key Vault',
  'kms-gcp':    'GCP Cloud KMS',
  'kms-vault':  'HashiCorp Vault',
  'on-prem':    'On-Premises (SSH / WinRM)',
  'hsm':        'HSM Partition (PKCS#11)',
  'ca':         'Certificate Authority',
  'cspm-tool':  'CSPM Tool (Prisma Cloud, AWS Security Hub)',
  'source-upload': 'Source Archive Upload (ZIP / tar.gz / rar / …)',
  'binary-upload': 'Binary Archive Upload (ELF / PE / Mach-O)',
  'network-upload': 'Network Capture Upload (.pcap / .pcapng / .cap / .snoop / .har)',
  'manual':     'Manual Entry',
}

function formatDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ── Scan Frequency Field ──────────────────────────────────────────────────────

const SIMPLE_PRESETS = [
  { label: '0m', value: '0m' },
  { label: '5m', value: '5m' },
  { label: '30m', value: '30m' },
  { label: '1h', value: '1h' },
  { label: '6h', value: '6h' },
  { label: '24h', value: '24h' },
]

const CRON_PRESETS = [
  { label: '*/15 * * * *', value: '*/15 * * * *' },
  { label: '0 * * * *', value: '0 * * * *' },
  { label: '0 */6 * * *', value: '0 */6 * * *' },
  { label: '0 0 * * *', value: '0 0 * * *' },
  { label: '0 0 * * 0', value: '0 0 * * 0' },
]

/** Returns 'simple' for duration strings like 6h / 30m, 'cron' for 5-field cron, or 'unknown'. */
function detectFormat(v: string): 'simple' | 'cron' | 'unknown' {
  if (/^\d+[smhd]$/.test(v.trim())) return 'simple'
  if (/^(\S+\s+){4}\S+$/.test(v.trim())) return 'cron'
  return 'unknown'
}

function ScanFrequencyField({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const fmt = detectFormat(value)

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-text-light dark:text-text-dark">
          Scan Frequency
        </label>
        {fmt !== 'unknown' && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-background-light dark:bg-background-dark text-subtle-light dark:text-subtle-dark font-mono">
            {fmt === 'simple' ? 'duration' : 'cron'}
          </span>
        )}
      </div>

      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. 6h  or  0 */6 * * *"
        spellCheck={false}
        className="w-full px-3 py-2 text-sm font-mono rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
      />

      {/* Simple duration presets */}
      <div className="mt-2 space-y-1.5">
        <p className="text-xs text-subtle-light dark:text-subtle-dark">Duration</p>
        <div className="flex flex-wrap gap-1.5">
          {SIMPLE_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => onChange(p.value)}
              className={`px-2 py-1 text-xs font-mono rounded-md border transition-colors ${
                value === p.value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border-light dark:border-border-dark text-subtle-light dark:text-subtle-dark hover:border-primary hover:text-primary'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Cron presets */}
        <p className="text-xs text-subtle-light dark:text-subtle-dark pt-0.5">Cron schedule</p>
        <div className="flex flex-wrap gap-1.5">
          {CRON_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => onChange(p.value)}
              className={`px-2 py-1 text-xs font-mono rounded-md border transition-colors ${
                value === p.value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border-light dark:border-border-dark text-subtle-light dark:text-subtle-dark hover:border-primary hover:text-primary'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <p className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">
        <span className="font-mono">0m</span> = manual only &nbsp;·&nbsp; Duration:{' '}
        <span className="font-mono">30m</span>, <span className="font-mono">6h</span>,{' '}
        <span className="font-mono">2d</span> &nbsp;·&nbsp; Cron:{' '}
        <span className="font-mono">minute hour day month weekday</span>
      </p>
    </div>
  )
}

// ── Scanner Picker ────────────────────────────────────────────────────────────

interface ScannerPickerProps {
  scanners: ScannerInfo[]
  modality: string
  sourceType: string
  selected: string[]
  onChange: (ids: string[]) => void
  scannerOptions?: Record<string, Record<string, unknown>>
  onOptionsChange?: (scannerId: string, opts: Record<string, unknown>) => void
}

function ScannerPicker({
  scanners, modality, sourceType, selected, onChange, scannerOptions, onOptionsChange,
}: ScannerPickerProps) {
  const compatible = scanners.filter((s) => s.modalities.includes(modality))

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id])

  if (compatible.length === 0) {
    return (
      <div className="text-xs text-subtle-light dark:text-subtle-dark italic">
        No scanners registered for modality "{modality}".
      </div>
    )
  }

  return (
    <div>
      <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
        Scanners
      </label>
      <p className="text-xs text-subtle-light dark:text-subtle-dark mb-2">
        Leave all unchecked to use the default scanner for this source type.
      </p>
      <div className="space-y-1.5">
        {compatible.map((s) => {
          const isDefault = s.default_for.includes(sourceType)
          const checked = selected.includes(s.id)
          // Count what applies to this source type, not what the scanner
          // declares overall — otherwise the badge promises options the
          // panel then does not show.
          const applicable = scannerOptionsFor(s, sourceType)
          const hasOptions = checked && applicable.length > 0
          return (
            <div key={s.id}>
              <label
                className={`flex items-center gap-3 px-3 py-2 border cursor-pointer transition-colors ${
                  hasOptions ? 'rounded-t-lg' : 'rounded-lg'
                } ${
                  checked
                    ? 'border-primary bg-primary/5'
                    : 'border-border-light dark:border-border-dark hover:border-primary/50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(s.id)}
                  className="accent-primary shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono text-text-light dark:text-text-dark">{s.id}</p>
                  {s.metadata?.description ? (
                    <p className="text-xs text-subtle-light dark:text-subtle-dark truncate">
                      {s.metadata.description}
                    </p>
                  ) : s.modalities.length > 0 ? (
                    <p className="text-xs text-subtle-light dark:text-subtle-dark">
                      {s.modalities.join(', ')}
                    </p>
                  ) : null}
                  {s.metadata?.tags && s.metadata.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {s.metadata.tags.slice(0, 4).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-1.5 py-0.5 rounded bg-background-light dark:bg-background-dark text-subtle-light dark:text-subtle-dark"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {applicable.length > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-background-light dark:bg-background-dark text-subtle-light dark:text-subtle-dark">
                      {applicable.length} option{applicable.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {isDefault && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 dark:bg-primary/15 text-primary dark:text-secondary">
                      default
                    </span>
                  )}
                </div>
              </label>

              {/* Inline options panel — expands when scanner is checked and has options */}
              {hasOptions && (
                <div className="px-4 py-3 border border-t-0 border-primary/30 rounded-b-lg bg-background-light dark:bg-background-dark">
                  <ScannerOptionsEditor
                    scanner={s}
                    sourceType={sourceType}
                    value={scannerOptions?.[s.id] ?? {}}
                    onChange={(v) => onOptionsChange?.(s.id, v)}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Source Config Fields (shared between Add and Edit modals) ─────────────────

type ConfigForm = {
  config_ca_cert: string; config_insecure_skip_verify: boolean
  config_url: string; config_branch: string; config_framework: string; config_mgmt_tool: string
  config_endpoint: string; config_project_id: string; config_auth_type: string
  config_protocol: string; config_tool: string
  config_scan_mode: string; config_host: string; config_user: string
  config_registry: string; config_repository: string; config_tag: string
  config_subnets: string
  config_network_mode: string
  config_interface: string
  config_ports: string
  config_node: string
  config_region: string; config_role_arn: string
  config_vault_url: string
  config_gcp_project: string; config_keyring: string; config_location: string
  config_address: string; config_mount_path: string
  config_onprem_protocol: string
  config_library_path: string; config_slot_id: string
}

function isValidSubnetOrDomain(entry: string): boolean {
  // Strip optional trailing :portspec (single "443", list "443,8443",
  // range "8000-8003", or mix "443,8000-8003"). CIDR entries may carry a
  // port suffix (10.0.0.0/24:8443). Anything not matching the portspec
  // grammar leaves the entry unchanged so host validation catches it.
  const colonIdx = entry.lastIndexOf(':')
  if (colonIdx > 0) {
    const suffix = entry.slice(colonIdx + 1)
    if (/^[\d,-]+$/.test(suffix)) {
      if (!isValidPortRange(suffix)) return false
      entry = entry.slice(0, colonIdx)
    }
  }
  const cidrMatch = entry.match(/^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\/(\d{1,2})$/)
  if (cidrMatch) {
    const octets = cidrMatch[1].split('.').map(Number)
    const prefix = parseInt(cidrMatch[2])
    return octets.every(o => o <= 255) && prefix <= 32
  }
  const ipMatch = entry.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/)
  if (ipMatch) {
    return [ipMatch[1], ipMatch[2], ipMatch[3], ipMatch[4]].map(Number).every(o => o <= 255)
  }
  return /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/.test(entry)
}

// isValidPortRange accepts single ports ("443"), comma-lists ("443,8443"),
// ranges ("8000-8003"), and mixes ("443,8000-8003"). Each numeric part must
// be 1-65535. Empty string is treated as valid (backend defaults to 443).
function isValidPortRange(input: string): boolean {
  const s = input.trim()
  if (s === '') return true
  const inRange = (n: number) => n >= 1 && n <= 65535
  for (const tok of s.split(',')) {
    const t = tok.trim()
    if (t === '') return false
    if (t.includes('-')) {
      const parts = t.split('-')
      if (parts.length !== 2) return false
      const lo = Number(parts[0].trim())
      const hi = Number(parts[1].trim())
      if (!Number.isInteger(lo) || !Number.isInteger(hi)) return false
      if (!inRange(lo) || !inRange(hi) || lo > hi) return false
    } else {
      const n = Number(t)
      if (!Number.isInteger(n) || !inRange(n)) return false
    }
  }
  return true
}

function inp(
  value: string,
  onChange: (v: string) => void,
  placeholder?: string,
  type = 'text'
) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
    />
  )
}

function sel(
  value: string,
  onChange: (v: string) => void,
  options: { value: string; label: string }[],
  id?: string
) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

function lbl(text: string) {
  return <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">{text}</label>
}

/**
 * Trust settings for a git-backed source, collapsed until needed.
 *
 * They matter only for a host the public roots do not cover — an internally
 * hosted GitLab or Bitbucket — so they sit behind a disclosure rather than
 * taking up room in the common case of cloning from github.com.
 */
function GitTLSFields({
  caCert,
  insecure,
  onCACert,
  onInsecure,
}: {
  caCert: string
  insecure: boolean
  onCACert: (v: string) => void
  onInsecure: (v: boolean) => void
}) {
  const configured = caCert.trim() !== '' || insecure

  return (
    <details open={configured} className="rounded-lg bg-background-light dark:bg-background-dark px-3 py-2">
      <summary className="text-sm font-medium text-text-light dark:text-text-dark cursor-pointer">
        TLS trust
        <span className="ml-2 text-xs font-normal text-subtle-light dark:text-subtle-dark">
          for a host with an internal certificate authority
        </span>
      </summary>

      <div className="mt-3 space-y-3">
        <div>
          {lbl('CA Certificate (PEM)')}
          <textarea
            value={caCert}
            onChange={(e) => onCACert(e.target.value)}
            rows={4}
            spellCheck={false}
            placeholder={'-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----'}
            className="w-full px-3 py-2 text-xs font-mono rounded-lg bg-surface-light dark:bg-container-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <p className="mt-1 text-xs text-subtle-light dark:text-subtle-dark">
            Trusted for this source in addition to the public roots and anything the
            deployment already trusts. Leave empty unless cloning fails to verify the
            server&apos;s certificate.
          </p>
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={insecure}
            onChange={(e) => onInsecure(e.target.checked)}
            className="mt-0.5 rounded text-primary focus:ring-primary/50"
          />
          <span className="text-xs">
            <span className="font-medium text-text-light dark:text-text-dark">
              Skip TLS verification
            </span>
            <span className="block text-subtle-light dark:text-subtle-dark">
              Accepts any certificate, so nothing proves the server is the one you meant —
              anyone able to intercept the connection can read the credentials sent with it.
              Prefer the CA certificate above.
            </span>
          </span>
        </label>
      </div>
    </details>
  )
}

/**
 * The TLS trust half of a git-backed source's config.
 *
 * Omits both keys when neither is set, so a source that never needed them does
 * not carry an empty certificate and an explicit false into the stored config.
 */
function gitTLSConfig(form: ConfigForm): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  const ca = form.config_ca_cert.trim()
  if (ca) out.ca_cert = ca
  if (form.config_insecure_skip_verify) out.insecure_skip_verify = true
  return out
}

function SourceConfigFields<T extends ConfigForm>({
  form,
  setForm,
  sourceType,
}: {
  form: T
  setForm: (f: T) => void
  sourceType: SourceType
}) {
  const set = (k: keyof ConfigForm) => (v: string) => setForm({ ...form, [k]: v })
  const t = sourceType

  // Trust settings are shared by every git-backed type, since all three are
  // cloned by the same code.
  const gitTLS = (
    <GitTLSFields
      caCert={form.config_ca_cert}
      insecure={form.config_insecure_skip_verify}
      onCACert={set('config_ca_cert')}
      onInsecure={(v) => setForm({ ...form, config_insecure_skip_verify: v })}
    />
  )

  if (t === 'git') return (
    <>
      <div>{lbl('Repository URL')}{inp(form.config_url, set('config_url'), 'https://github.com/org/repo')}</div>
      <div>{lbl('Branch')}{inp(form.config_branch, set('config_branch'), 'main')}</div>
      {gitTLS}
    </>
  )

  if (t === 'iac') return (
    <>
      <div>{lbl('Repository URL')}{inp(form.config_url, set('config_url'), 'https://github.com/org/infra')}</div>
      <div>{lbl('Branch')}{inp(form.config_branch, set('config_branch'), 'main')}</div>
      <div>{lbl('Framework')}{sel(form.config_framework, set('config_framework'), [
        { value: 'terraform', label: 'Terraform' },
        { value: 'cloudformation', label: 'CloudFormation' },
        { value: 'pulumi', label: 'Pulumi' },
      ])}</div>
      {gitTLS}
    </>
  )

  if (t === 'config-mgmt') return (
    <>
      <div>{lbl('Repository URL')}{inp(form.config_url, set('config_url'), 'https://github.com/org/playbooks')}</div>
      <div>{lbl('Branch')}{inp(form.config_branch, set('config_branch'), 'main')}</div>
      <div>{lbl('Tool')}{sel(form.config_mgmt_tool, set('config_mgmt_tool'), [
        { value: 'ansible', label: 'Ansible' },
        { value: 'chef', label: 'Chef' },
        { value: 'puppet', label: 'Puppet' },
      ])}</div>
      {gitTLS}
    </>
  )

  if (t === 'sast-tool' || t === 'sca-tool') return (
    <>
      <div>{lbl('Tool API Endpoint')}{inp(form.config_endpoint, set('config_endpoint'), 'https://sonarqube.example.com')}</div>
      <div>{lbl('Project ID (optional)')}{inp(form.config_project_id, set('config_project_id'), 'my-project')}</div>
    </>
  )

  if (t === 'runtime') return (
    <>
      <div>{lbl('Scan Mode')}{sel(form.config_scan_mode, set('config_scan_mode'), [
        { value: 'ssh', label: 'SSH' },
        { value: 'agent', label: 'Sidecar Agent' },
        { value: 'ebpf', label: 'eBPF (Linux only)' },
        { value: 'k8s-exec', label: 'kubectl exec' },
      ])}</div>
      <div>{lbl('Host')}{inp(form.config_host, set('config_host'), '10.0.0.1')}</div>
      <div>{lbl('User (optional)')}{inp(form.config_user, set('config_user'), 'ubuntu')}</div>
    </>
  )

  if (t === 'container') return (
    <>
      <div>{lbl('Registry')}{inp(form.config_registry, set('config_registry'), 'docker.io')}</div>
      <div>{lbl('Repository')}{inp(form.config_repository, set('config_repository'), 'org/image-name')}</div>
      <div>{lbl('Tag (optional)')}{inp(form.config_tag, set('config_tag'), 'latest')}</div>
    </>
  )

  if (t === 'network') return (
    <>
      <div>
        {lbl('Scan Mode')}
        {sel(form.config_network_mode, set('config_network_mode'), [
          { value: 'probe', label: 'Probe targets from the server' },
          { value: 'live', label: 'Live monitor — an agent on the host uploads what it sees' },
        ], 'network-mode-select')}
      </div>
      {form.config_network_mode === 'live' ? (
        <>
          <div className="text-xs text-subtle-light dark:text-subtle-dark bg-container-light dark:bg-container-dark rounded-lg px-3 py-2">
            A live monitor watches real traffic on its host and uploads its
            inventory continuously. The server never scans it — there is nothing
            to connect to — so this source has no targets. Run{' '}
            <span className="font-mono">qtz-discovery-cli scan network --listen</span>{' '}
            on the host (or deploy it as a DaemonSet); it registers itself and
            fills in the details below on its first upload.
          </div>
          <div>{lbl('Node (optional \u2014 reported by the agent)')}{inp(form.config_node, set('config_node'), 'node-1')}</div>
          <div>{lbl('Interface (optional \u2014 reported by the agent)')}{inp(form.config_interface, set('config_interface'), 'any')}</div>
          <div>{lbl('Capture Ports (optional \u2014 reported by the agent)')}{inp(form.config_ports, set('config_ports'), '443,8443,22')}</div>
        </>
      ) : (
        <div>
          {lbl('Network Targets (one per line: host[:port] where port is 443, 443,8443, or 8000-8003)')}
          <textarea
            value={form.config_subnets}
            onChange={(e) => set('config_subnets')(e.target.value)}
            placeholder={'example.com:443\napi.example.com:443,8443,8000-8003\n10.0.0.0/24:9443\nscanme.nmap.org:22'}
            rows={5}
            className="w-full px-3 py-2 text-sm font-mono rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
      )}
    </>
  )

  if (t === 'api') return (
    <>
      <div>{lbl('API Base URL')}{inp(form.config_endpoint, set('config_endpoint'), 'https://api.example.com')}</div>
      <div>{lbl('Auth Type')}{sel(form.config_auth_type, set('config_auth_type'), [
        { value: 'none', label: 'None' },
        { value: 'bearer', label: 'Bearer Token' },
        { value: 'basic', label: 'Basic Auth' },
        { value: 'apikey', label: 'API Key' },
      ])}</div>
    </>
  )

  if (t === 'kms-aws') return (
    <>
      <div>{lbl('AWS Region')}{inp(form.config_region, set('config_region'), 'us-east-1')}</div>
      <div>{lbl('IAM Role ARN (optional)')}{inp(form.config_role_arn, set('config_role_arn'), 'arn:aws:iam::123456789:role/name')}</div>
    </>
  )

  if (t === 'kms-azure') return (
    <div>{lbl('Key Vault URL')}{inp(form.config_vault_url, set('config_vault_url'), 'https://myvault.vault.azure.net')}</div>
  )

  if (t === 'kms-gcp') return (
    <>
      <div>{lbl('GCP Project')}{inp(form.config_gcp_project, set('config_gcp_project'), 'my-project-id')}</div>
      <div>{lbl('Keyring (optional)')}{inp(form.config_keyring, set('config_keyring'), 'my-keyring')}</div>
      <div>{lbl('Location (optional)')}{inp(form.config_location, set('config_location'), 'global')}</div>
    </>
  )

  if (t === 'kms-vault') return (
    <>
      <div>{lbl('Vault Address')}{inp(form.config_address, set('config_address'), 'https://vault.example.com')}</div>
      <div>{lbl('Mount Path (optional)')}{inp(form.config_mount_path, set('config_mount_path'), 'secret')}</div>
    </>
  )

  if (t === 'on-prem') return (
    <>
      <div>{lbl('Host')}{inp(form.config_host, set('config_host'), '10.0.0.1')}</div>
      <div>{lbl('Protocol')}{sel(form.config_onprem_protocol, set('config_onprem_protocol'), [
        { value: 'ssh', label: 'SSH' },
        { value: 'winrm', label: 'WinRM' },
        { value: 'agent', label: 'Agent' },
      ])}</div>
      <div>{lbl('User (optional)')}{inp(form.config_user, set('config_user'), 'admin')}</div>
    </>
  )

  if (t === 'hsm') return (
    <>
      <div>{lbl('PKCS#11 Library Path')}{inp(form.config_library_path, set('config_library_path'), '/usr/lib/pkcs11/libsofthsm2.so')}</div>
      <div>{lbl('Slot ID')}{inp(form.config_slot_id, set('config_slot_id'), '0')}</div>
    </>
  )

  if (t === 'ca') return (
    <>
      <div>{lbl('Endpoint')}{inp(form.config_endpoint, set('config_endpoint'), 'https://ca.example.com')}</div>
      <div>{lbl('Protocol')}{sel(form.config_protocol, set('config_protocol'), [
        { value: 'rest', label: 'REST API' },
        { value: 'ldap', label: 'LDAP' },
      ])}</div>
    </>
  )

  if (t === 'cspm-tool') return (
    <>
      <div>{lbl('Tool API Endpoint')}{inp(form.config_endpoint, set('config_endpoint'), 'https://api.prismacloud.io')}</div>
      <div>{lbl('Tool')}{sel(form.config_tool, set('config_tool'), [
        { value: 'prismacloud', label: 'Prisma Cloud' },
        { value: 'aws-security-hub', label: 'AWS Security Hub' },
      ])}</div>
      <div>{lbl('Project / Account ID (optional)')}{inp(form.config_project_id, set('config_project_id'))}</div>
    </>
  )

  // source-upload / binary-upload / manual — no config needed
  return null
}

/**
 * Whether the form's current selection describes a source the server will
 * schedule. Reads the in-progress form rather than a saved Source, since the
 * network mode can be switched between live and probe before saving.
 */
function isSchedulableSelection(sourceType: SourceType, networkMode: string): boolean {
  if (isUploadDrivenType(sourceType)) return false
  return !(sourceType === 'network' && networkMode === 'live')
}

/** Stands in for the frequency field on a source nothing schedules. */
function UnscheduledNotice({ sourceType }: { sourceType: SourceType }) {
  const manual = sourceType === 'manual'
  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-background-light dark:bg-background-dark text-xs text-subtle-light dark:text-subtle-dark">
      <span className="material-icons text-sm mt-0.5">schedule</span>
      <span>
        <span className="font-medium text-text-light dark:text-text-dark">
          No scan frequency.
        </span>{' '}
        {manual
          ? 'Assets on a manual source are entered by hand; nothing is scanned.'
          : sourceType === 'network'
            ? 'A live monitor is scanned from what its agent uploads, as each upload arrives.'
            : 'This source is scanned when a file is uploaded to it. Re-scanning means uploading again.'}
      </span>
    </div>
  )
}

// ── Add Source Modal ──────────────────────────────────────────────────────────

interface AddSourceModalProps {
  onClose: () => void
  orgId: string
  onCreated?: (sourceId: string) => void
}

export function AddSourceModal({ onClose, orgId, onCreated }: AddSourceModalProps) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: '',
    description: '',
    modality: 'source' as Modality,
    source_type: 'git' as SourceType,
    scan_frequency: '1h',
    // git / iac / config-mgmt
    // git / iac / config-mgmt TLS trust
    config_ca_cert: '',
    config_insecure_skip_verify: false,
    config_url: '',
    config_branch: '',
    config_framework: 'terraform',
    config_mgmt_tool: 'ansible',
    // sast-tool / sca-tool / api / ca / cspm-tool
    config_endpoint: '',
    config_project_id: '',
    config_auth_type: 'none',
    config_protocol: 'rest',
    config_tool: 'prismacloud',
    // runtime
    config_scan_mode: 'ssh',
    config_host: '',
    config_user: '',
    // container
    config_registry: '',
    config_repository: '',
    config_tag: '',
    // network
    config_subnets: '',
    config_network_mode: 'probe',
    config_interface: '',
    config_ports: '',
    config_node: '',
    // kms-aws
    config_region: '',
    config_role_arn: '',
    // kms-azure
    config_vault_url: '',
    // kms-gcp
    config_gcp_project: '',
    config_keyring: '',
    config_location: '',
    // kms-vault
    config_address: '',
    config_mount_path: '',
    // on-prem
    config_onprem_protocol: 'ssh',
    // hsm
    config_library_path: '',
    config_slot_id: '0',
  })
  const [scannerIds, setScannerIds] = useState<string[]>([])
  const [scannerOptions, setScannerOptions] = useState<Record<string, Record<string, unknown>>>({})
  const [error, setError] = useState('')

  // Upload and live-monitor sources are scanned when their data arrives, so
  // the server never reads their scan_frequency. Offering the field would
  // invite a setting that silently does nothing.
  const schedulable = isSchedulableSelection(form.source_type, form.config_network_mode)

  const { data: scannersResp } = useQuery({
    queryKey: ['scanners'],
    queryFn: () => scannersApi.list(),
    staleTime: 1000 * 60 * 5,
  })
  const scanners = useMemo(() => scannersResp?.data ?? [], [scannersResp?.data])

  // Pre-check the default scanner(s) for the current source type.
  // Resets when source_type changes so selection stays coherent.
  useEffect(() => {
    if (scanners.length === 0) return
    const defaults = scanners
      .filter((s) => s.default_for.includes(form.source_type))
      .map((s) => s.id)
    setScannerIds(defaults)
    // Options are scoped to the source type, so any already set belong to the
    // old one. Carrying them over would submit a value the API now rejects,
    // for a field the form no longer shows.
    setScannerOptions({})
  }, [scanners, form.source_type])

  const availableTypes = SOURCE_TYPES_BY_MODALITY[form.modality]

  const mutation = useMutation({
    mutationFn: (req: CreateSourceRequest) => sourcesApi.create(orgId, req),
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ['sources', orgId] })
      onCreated?.(resp.data.id)
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  const buildConfig = (): Record<string, unknown> => {
    const t = form.source_type
    if (t === 'git') return { url: form.config_url, branch: form.config_branch || undefined, ...gitTLSConfig(form) }
    if (t === 'iac') return { url: form.config_url, branch: form.config_branch || undefined, framework: form.config_framework || undefined, ...gitTLSConfig(form) }
    if (t === 'config-mgmt') return { url: form.config_url, branch: form.config_branch || undefined, tool: form.config_mgmt_tool, ...gitTLSConfig(form) }
    if (t === 'sast-tool' || t === 'sca-tool') return { endpoint: form.config_endpoint, project_id: form.config_project_id || undefined }
    if (t === 'runtime') return { scan_mode: form.config_scan_mode, host: form.config_host || undefined, user: form.config_user || undefined }
    if (t === 'container') return { registry: form.config_registry, repository: form.config_repository, tag: form.config_tag || undefined }
    if (t === 'network') {
      // The mode is written explicitly, including for probe: it is the setting
      // the server reads to decide whether it scans this source itself or
      // waits for an agent, so leaving it implicit hides the decision.
      if (form.config_network_mode === 'live') {
        return {
          mode: 'live',
          subnets: [],
          node: form.config_node.trim() || undefined,
          interface: form.config_interface.trim() || undefined,
          ports: form.config_ports.trim() || undefined,
        }
      }
      return {
        mode: 'probe',
        subnets: form.config_subnets.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
      }
    }
    if (t === 'api') return { endpoint: form.config_endpoint, auth_type: form.config_auth_type || undefined }
    if (t === 'kms-aws') return { region: form.config_region, role_arn: form.config_role_arn || undefined }
    if (t === 'kms-azure') return { vault_url: form.config_vault_url }
    if (t === 'kms-gcp') return { project: form.config_gcp_project, keyring: form.config_keyring || undefined, location: form.config_location || undefined }
    if (t === 'kms-vault') return { address: form.config_address, mount_path: form.config_mount_path || undefined }
    if (t === 'on-prem') return { host: form.config_host, protocol: form.config_onprem_protocol, user: form.config_user || undefined }
    if (t === 'hsm') return { library_path: form.config_library_path, slot_id: parseInt(form.config_slot_id) || 0 }
    if (t === 'ca') return { endpoint: form.config_endpoint, protocol: form.config_protocol }
    if (t === 'cspm-tool') return { endpoint: form.config_endpoint, tool: form.config_tool, project_id: form.config_project_id || undefined }
    return {}
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('Name is required')
      return
    }
    // A live monitor has no targets by design, so the target requirement
    // applies to probe mode only.
    if (form.source_type === 'network' && form.config_network_mode !== 'live') {
      const entries = form.config_subnets.split(/\r?\n/).map(s => s.trim()).filter(Boolean)
      if (entries.length === 0) {
        setError('At least one network target is required')
        return
      }
      const invalid = entries.find(e => !isValidSubnetOrDomain(e))
      if (invalid) {
        setError(`Invalid target: "${invalid}" (expected host[:port] where port is 443, 443,8443, or 8000-8003)`)
        return
      }
    }
    mutation.mutate({
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      modality: form.modality,
      source_type: form.source_type,
      scan_frequency: (schedulable && form.scan_frequency && form.scan_frequency !== '0m')
        ? form.scan_frequency
        : undefined,
      config: buildConfig(),
      scanner_ids: scannerIds.length > 0 ? scannerIds : undefined,
      scanner_options: Object.keys(scannerOptions).length > 0 ? scannerOptions : undefined,
    })
  }

  const st = form.source_type

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className="relative bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-float dark:shadow-none dark:border dark:border-border-dark w-full max-w-lg max-h-[90vh] flex flex-col"
        id="add-source-modal"
      >
        {/* Fixed header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark shrink-0">
          <h2 className="text-lg font-semibold text-text-light dark:text-text-dark">
            Add Discovery Source
          </h2>
          <button
            onClick={onClose}
            className="text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark"
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1 p-6 space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-error/10 dark:bg-error/15 text-error rounded-xl text-sm">
                <span className="material-icons text-base">error</span>
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
                Name <span className="text-error">*</span>
              </label>
              <input
                id="source-name-input"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Payment Service Git Repo"
                className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
                Description
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="Optional description"
                className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
                  Modality
                </label>
                <select
                  id="source-modality-select"
                  value={form.modality}
                  onChange={(e) => {
                    const mod = e.target.value as Modality
                    const types = SOURCE_TYPES_BY_MODALITY[mod]
                    setForm({ ...form, modality: mod, source_type: types[0] })
                  }}
                  className="w-full pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {MODALITIES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
                  Source Type
                </label>
                <select
                  id="source-type-select"
                  value={form.source_type}
                  onChange={(e) => setForm({ ...form, source_type: e.target.value as SourceType })}
                  className="w-full pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {availableTypes.map((t) => (
                    <option key={t} value={t}>
                      {SOURCE_TYPE_LABELS[t] ?? t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* ── Conditional config fields ──────────────────────── */}
            <SourceConfigFields form={form} setForm={setForm} sourceType={st} />

            {schedulable ? (
              <ScanFrequencyField
                value={form.scan_frequency}
                onChange={(v) => setForm({ ...form, scan_frequency: v })}
              />
            ) : (
              <UnscheduledNotice sourceType={st} />
            )}

            {scanners.length > 0 && (
              <ScannerPicker
                scanners={scanners}
                modality={form.modality}
                sourceType={form.source_type}
                selected={scannerIds}
                onChange={setScannerIds}
                scannerOptions={scannerOptions}
                onOptionsChange={(id, v) => setScannerOptions((prev) => ({ ...prev, [id]: v }))}
              />
            )}
          </div>

          {/* Pinned footer */}
          <div className="flex gap-3 px-6 py-4 border-t border-border-light dark:border-border-dark shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm rounded-lg hover:bg-background-light dark:hover:bg-background-dark text-text-light dark:text-text-dark"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <span className="material-icons text-base animate-spin">sync</span>
                  Creating…
                </>
              ) : (
                'Create Source'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Edit Source Modal ─────────────────────────────────────────────────────────

interface EditSourceModalProps {
  source: Source
  onClose: () => void
  orgId: string
}

function EditSourceModal({ source, onClose, orgId }: EditSourceModalProps) {
  const queryClient = useQueryClient()

  const c = source.config ?? {}
  const str = (k: string) => (typeof c[k] === 'string' ? (c[k] as string) : '')
  const num = (k: string) => (typeof c[k] === 'number' ? String(c[k]) : '0')

  const [form, setForm] = useState({
    name: source.name,
    description: source.description ?? '',
    source_type: source.source_type,
    scan_frequency: source.scan_frequency,
    is_active: source.is_active,
    // git / iac / config-mgmt
    config_ca_cert: str('ca_cert'),
    config_insecure_skip_verify: c['insecure_skip_verify'] === true,
    config_url: str('url'),
    config_branch: str('branch'),
    config_framework: str('framework') || 'terraform',
    config_mgmt_tool: str('tool') || 'ansible',
    // sast-tool / sca-tool / api / ca / cspm-tool
    config_endpoint: str('endpoint'),
    config_project_id: str('project_id'),
    config_auth_type: str('auth_type') || 'none',
    config_protocol: str('protocol') || 'rest',
    config_tool: str('tool') || 'prismacloud',
    // runtime
    config_scan_mode: str('scan_mode') || 'ssh',
    config_host: str('host'),
    config_user: str('user'),
    // container
    config_registry: str('registry'),
    config_repository: str('repository'),
    config_tag: str('tag'),
    // network — hydrate as newline-separated. Legacy sources with a
    // separate `port_range` field get it projected onto bare lines so
    // behavior is preserved after re-save without the extra config key.
    config_subnets: (() => {
      const raw = Array.isArray(c.subnets) ? (c.subnets as string[]) : []
      const legacyPort = str('port_range').trim()
      const lines = legacyPort
        ? raw.map((s) => /:[\d,-]+$/.test(s) ? s : `${s}:${legacyPort}`)
        : raw
      return lines.join('\n')
    })(),
    // An absent mode means probe — that is what every network source created
    // before live monitoring existed is, and what the server assumes.
    config_network_mode: str('mode') === 'live' ? 'live' : 'probe',
    config_interface: str('interface'),
    config_ports: str('ports'),
    config_node: str('node'),
    // kms-aws
    config_region: str('region'),
    config_role_arn: str('role_arn'),
    // kms-azure
    config_vault_url: str('vault_url'),
    // kms-gcp
    config_gcp_project: str('project'),
    config_keyring: str('keyring'),
    config_location: str('location'),
    // kms-vault
    config_address: str('address'),
    config_mount_path: str('mount_path'),
    // on-prem
    config_onprem_protocol: str('protocol') || 'ssh',
    // hsm
    config_library_path: str('library_path'),
    config_slot_id: num('slot_id'),
  })
  const [scannerIds, setScannerIds] = useState<string[]>(source.scanner_ids ?? [])
  const [scannerOptions, setScannerOptions] = useState<Record<string, Record<string, unknown>>>(
    source.scanner_options ?? {}
  )
  const [error, setError] = useState('')

  // Read from the form, not the saved source: a network source can be switched
  // between probe and live here, and that is what decides whether a frequency
  // means anything.
  const schedulable = isSchedulableSelection(source.source_type, form.config_network_mode)

  const { data: scannersResp } = useQuery({
    queryKey: ['scanners'],
    queryFn: () => scannersApi.list(),
    staleTime: 1000 * 60 * 5,
  })
  const scanners = useMemo(() => scannersResp?.data ?? [], [scannersResp?.data])

  const mutation = useMutation({
    mutationFn: (req: UpdateSourceRequest) => sourcesApi.update(orgId, source.id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources', orgId] })
      queryClient.invalidateQueries({ queryKey: ['source', orgId, source.id] })
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  const buildConfig = (): Record<string, unknown> => {
    // Reuse the same logic as AddSourceModal via a thin shim
    const t = source.source_type
    if (t === 'git') return { url: form.config_url, branch: form.config_branch || undefined, ...gitTLSConfig(form) }
    if (t === 'iac') return { url: form.config_url, branch: form.config_branch || undefined, framework: form.config_framework || undefined, ...gitTLSConfig(form) }
    if (t === 'config-mgmt') return { url: form.config_url, branch: form.config_branch || undefined, tool: form.config_mgmt_tool, ...gitTLSConfig(form) }
    if (t === 'sast-tool' || t === 'sca-tool') return { endpoint: form.config_endpoint, project_id: form.config_project_id || undefined }
    if (t === 'runtime') return { scan_mode: form.config_scan_mode, host: form.config_host || undefined, user: form.config_user || undefined }
    if (t === 'container') return { registry: form.config_registry, repository: form.config_repository, tag: form.config_tag || undefined }
    if (t === 'network') {
      // The mode is written explicitly, including for probe: it is the setting
      // the server reads to decide whether it scans this source itself or
      // waits for an agent, so leaving it implicit hides the decision.
      if (form.config_network_mode === 'live') {
        return {
          mode: 'live',
          subnets: [],
          node: form.config_node.trim() || undefined,
          interface: form.config_interface.trim() || undefined,
          ports: form.config_ports.trim() || undefined,
        }
      }
      return {
        mode: 'probe',
        subnets: form.config_subnets.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
      }
    }
    if (t === 'api') return { endpoint: form.config_endpoint, auth_type: form.config_auth_type || undefined }
    if (t === 'kms-aws') return { region: form.config_region, role_arn: form.config_role_arn || undefined }
    if (t === 'kms-azure') return { vault_url: form.config_vault_url }
    if (t === 'kms-gcp') return { project: form.config_gcp_project, keyring: form.config_keyring || undefined, location: form.config_location || undefined }
    if (t === 'kms-vault') return { address: form.config_address, mount_path: form.config_mount_path || undefined }
    if (t === 'on-prem') return { host: form.config_host, protocol: form.config_onprem_protocol, user: form.config_user || undefined }
    if (t === 'hsm') return { library_path: form.config_library_path, slot_id: parseInt(form.config_slot_id) || 0 }
    if (t === 'ca') return { endpoint: form.config_endpoint, protocol: form.config_protocol }
    if (t === 'cspm-tool') return { endpoint: form.config_endpoint, tool: form.config_tool, project_id: form.config_project_id || undefined }
    return source.config
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('Name is required')
      return
    }
    // A live monitor has no targets by design, so the target requirement
    // applies to probe mode only.
    if (form.source_type === 'network' && form.config_network_mode !== 'live') {
      const entries = form.config_subnets.split(/\r?\n/).map(s => s.trim()).filter(Boolean)
      if (entries.length === 0) {
        setError('At least one network target is required')
        return
      }
      const invalid = entries.find(e => !isValidSubnetOrDomain(e))
      if (invalid) {
        setError(`Invalid target: "${invalid}" (expected host[:port] where port is 443, 443,8443, or 8000-8003)`)
        return
      }
    }
    mutation.mutate({
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      scan_frequency: schedulable ? form.scan_frequency : undefined,
      is_active: form.is_active,
      config: buildConfig(),
      scanner_ids: scannerIds,
      scanner_options: Object.keys(scannerOptions).length > 0 ? scannerOptions : undefined,
    })
  }


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-float dark:shadow-none dark:border dark:border-border-dark w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark shrink-0">
          <h2 className="text-lg font-semibold text-text-light dark:text-text-dark">
            Edit Source
          </h2>
          <button
            onClick={onClose}
            className="text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark"
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-error/10 dark:bg-error/15 text-error rounded-xl text-sm">
                <span className="material-icons text-base">error</span>
                {error}
              </div>
            )}

            {/* Read-only info */}
            <div className="flex gap-3 p-3 bg-background-light dark:bg-background-dark rounded-lg text-xs text-subtle-light dark:text-subtle-dark">
              <span>
                <span className="font-medium">Modality:</span> {source.modality}
              </span>
              <span>·</span>
              <span>
                <span className="font-medium">Type:</span> {source.source_type}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
                Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
                Description
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="Optional description"
                className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
              />
            </div>

            <SourceConfigFields form={form} setForm={setForm} sourceType={source.source_type} />

            {schedulable ? (
              <ScanFrequencyField
                value={form.scan_frequency}
                onChange={(v) => setForm({ ...form, scan_frequency: v })}
              />
            ) : (
              <UnscheduledNotice sourceType={source.source_type} />
            )}

            <div className="flex items-center justify-between py-1">
              <label className="text-sm font-medium text-text-light dark:text-text-dark">
                Active
              </label>
              <button
                type="button"
                role="switch"
                aria-checked={form.is_active}
                onClick={() => setForm({ ...form, is_active: !form.is_active })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  form.is_active ? 'bg-primary' : 'bg-border-light dark:bg-border-dark'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form.is_active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {scanners.length > 0 && (
              <ScannerPicker
                scanners={scanners}
                modality={source.modality}
                sourceType={source.source_type}
                selected={scannerIds}
                onChange={setScannerIds}
                scannerOptions={scannerOptions}
                onOptionsChange={(id, v) => setScannerOptions((prev) => ({ ...prev, [id]: v }))}
              />
            )}
          </div>

          <div className="flex gap-3 px-6 py-4 border-t border-border-light dark:border-border-dark shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm rounded-lg hover:bg-background-light dark:hover:bg-background-dark text-text-light dark:text-text-dark"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <span className="material-icons text-base animate-spin">sync</span>
                  Saving…
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Sources Page ──────────────────────────────────────────────────────────────

export default function Sources() {
  const { currentOrgId } = useOrgStore()
  const { sourceFilters, setSourceFilters } = useDiscoveryStore()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const [showModal, setShowModal] = useState(searchParams.get('new') === 'true')
  const [editingSource, setEditingSource] = useState<Source | null>(null)

  const { data: resp, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['sources', currentOrgId],
    queryFn: () => sourcesApi.list(currentOrgId),
  })

  const { data: inventoriesResp } = useQuery({
    queryKey: ['inventories', currentOrgId],
    queryFn: () => inventoryApi.list(currentOrgId),
    enabled: !!currentOrgId,
  })
  const inventories: Inventory[] = inventoriesResp?.inventories ?? []

  const inventoryDetailQueries = useQueries({
    queries: inventories.map(inv => ({
      queryKey: ['inventory', currentOrgId, inv.id],
      queryFn: () => inventoryApi.get(currentOrgId, inv.id),
    })),
  })

  const sourceInventoryMap = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>()
    inventoryDetailQueries.forEach(({ data: inv }) => {
      if (!inv) return
      inv.sources.forEach(src => {
        map.set(src.id, { id: inv.id, name: inv.name })
      })
    })
    return map
  }, [inventoryDetailQueries])

  const [assigningSource, setAssigningSource] = useState<Source | null>(null)
  const [assignInventoryId, setAssignInventoryId] = useState('')
  const [assignFilter, setAssignFilter] = useState('')

  const closeAssignModal = () => { setAssigningSource(null); setAssignInventoryId(''); setAssignFilter('') }

  const addToInventoryMutation = useMutation({
    mutationFn: ({ inventoryId, sourceId }: { inventoryId: string; sourceId: string }) =>
      inventoryApi.addSources(currentOrgId, inventoryId, { source_ids: [sourceId] }),
    onSuccess: (_data, { inventoryId }) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', currentOrgId, inventoryId] })
      closeAssignModal()
    },
  })

  const removeFromInventoryMutation = useMutation({
    mutationFn: ({ inventoryId, sourceId }: { inventoryId: string; sourceId: string }) =>
      inventoryApi.removeSource(currentOrgId, inventoryId, sourceId),
    onSuccess: (_data, { inventoryId }) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', currentOrgId, inventoryId] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => sourcesApi.delete(currentOrgId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sources', currentOrgId] }),
  })

  const scanMutation = useMutation({
    mutationFn: (sourceId: string) => scansApi.create(currentOrgId, { source_id: sourceId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scans', currentOrgId] }),
  })

  const sources = resp?.data ?? []

  const filtered = sources.filter((s) => {
    if (
      sourceFilters.search &&
      !s.name.toLowerCase().includes(sourceFilters.search.toLowerCase()) &&
      !s.description.toLowerCase().includes(sourceFilters.search.toLowerCase())
    )
      return false
    if (sourceFilters.modality && s.modality !== sourceFilters.modality) return false
    if (sourceFilters.source_type && s.source_type !== sourceFilters.source_type) return false
    if (sourceFilters.is_active === 'true' && !s.is_active) return false
    if (sourceFilters.is_active === 'false' && s.is_active) return false
    return true
  })

  return (
    <div className="space-y-6" id="sources-page">
      {showModal && (
        <AddSourceModal orgId={currentOrgId} onClose={() => setShowModal(false)} />
      )}
      {editingSource && (
        <EditSourceModal
          source={editingSource}
          orgId={currentOrgId}
          onClose={() => setEditingSource(null)}
        />
      )}

      {assigningSource && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={closeAssignModal} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-2xl shadow-float dark:shadow-none dark:border dark:border-border-dark p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-text-light dark:text-text-dark">Assign to Inventory</h2>
                  <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">{assigningSource.name}</p>
                </div>
                <button onClick={closeAssignModal} className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5">
                  <span className="material-icons text-xl">close</span>
                </button>
              </div>

              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background-light dark:bg-background-dark">
                <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">search</span>
                <input
                  autoFocus
                  type="text"
                  placeholder="Filter inventories…"
                  value={assignFilter}
                  onChange={(e) => setAssignFilter(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark border-0 outline-none focus:outline-none focus:ring-0 focus:ring-offset-0 focus:[box-shadow:none] focus:border-transparent"
                />
                {assignFilter && (
                  <button onClick={() => setAssignFilter('')} className="text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark">
                    <span className="material-icons text-base">close</span>
                  </button>
                )}
              </div>

              {inventories.length === 0 ? (
                <p className="text-sm text-subtle-light dark:text-subtle-dark">No inventories found.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {inventories
                    .filter(inv => !assignFilter || inv.name.toLowerCase().includes(assignFilter.toLowerCase()))
                    .map(inv => {
                      const isCurrentlyAssigned = sourceInventoryMap.get(assigningSource.id)?.id === inv.id
                      return (
                        <label
                          key={inv.id}
                          className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${assignInventoryId === inv.id ? 'bg-primary/10 dark:bg-primary/15' : 'bg-container-light dark:bg-surface-dark hover:bg-primary/5 dark:hover:bg-primary/10'}`}
                        >
                          <input
                            type="radio"
                            name="assign-inventory"
                            checked={assignInventoryId === inv.id}
                            onChange={() => setAssignInventoryId(inv.id)}
                            className="w-4 h-4 accent-primary"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">{inv.name}</p>
                            <p className="text-xs text-subtle-light dark:text-subtle-dark">{inv.source_count} source{inv.source_count !== 1 ? 's' : ''}</p>
                          </div>
                          {isCurrentlyAssigned && (
                            <span className="text-xs font-medium text-primary dark:text-secondary">Current</span>
                          )}
                        </label>
                      )
                    })}
                </div>
              )}

              <div className="flex justify-end items-center gap-3 pt-1">
                <button onClick={closeAssignModal} className="px-4 py-2 text-sm text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark">
                  Cancel
                </button>
                <button
                  onClick={() => addToInventoryMutation.mutate({ inventoryId: assignInventoryId, sourceId: assigningSource.id })}
                  disabled={!assignInventoryId || addToInventoryMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  {addToInventoryMutation.isPending && (
                    <span className="material-icons text-base animate-spin">autorenew</span>
                  )}
                  Assign
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {isError && (
        <div className="flex items-start gap-3 p-4 bg-error/5 dark:bg-error/10 border border-error/20 dark:border-error/25 rounded-xl">
          <span className="material-icons text-error text-xl mt-0.5">error_outline</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-error">
              Failed to load sources
            </p>
            <p className="text-sm text-error/80 mt-0.5 font-mono">
              {(error as Error)?.message ?? 'Unknown error'}
            </p>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1">
              Check that <code className="font-mono">VITE_API_URL</code> in your{' '}
              <code className="font-mono">.env</code> points to the running qtz-discovery backend
              and that the organisation ID is correct.
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="text-sm text-error hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">
            Discovery Sources
          </h1>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
            {sources.length} source{sources.length !== 1 ? 's' : ''} configured
          </p>
        </div>
        <button
          id="add-source-btn"
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium"
        >
          <span className="material-icons text-base">add</span>
          Add Discovery Source
        </button>
      </div>

      {/* Filters */}
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark">
        <div className="flex flex-wrap gap-3">
          <input
            id="source-search"
            type="text"
            placeholder="Search discovery sources…"
            value={sourceFilters.search}
            onChange={(e) => setSourceFilters({ search: e.target.value })}
            className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50 w-56"
          />
          <select
            id="source-modality-filter"
            value={sourceFilters.modality}
            onChange={(e) => setSourceFilters({ modality: e.target.value as Modality | '' })}
            className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">All Modalities</option>
            {MODALITIES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <select
            value={sourceFilters.is_active}
            onChange={(e) =>
              setSourceFilters({ is_active: e.target.value as 'all' | 'true' | 'false' })
            }
            className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" id="sources-table">
            <thead className="bg-container-light dark:bg-container-dark">
              <tr>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Name
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Modality
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Type
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Status
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Credential
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Last Scanned
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Frequency
                </th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">
                  Inventory
                </th>
                <th className="px-6 py-3 text-right label-sm text-subtle-light dark:text-subtle-dark">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-10 text-center">
                    <span className="material-icons animate-spin text-primary text-2xl">sync</span>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-10 text-center text-subtle-light dark:text-subtle-dark"
                  >
                    {sources.length === 0 ? (
                      <div className="flex flex-col items-center gap-2">
                        <span className="material-icons text-4xl opacity-40">radar</span>
                        <p>No sources configured yet.</p>
                        <button
                          onClick={() => setShowModal(true)}
                          className="text-primary hover:underline text-sm"
                        >
                          Add your first source
                        </button>
                      </div>
                    ) : (
                      'No sources match the current filters.'
                    )}
                  </td>
                </tr>
              ) : (
                filtered.map((source) => (
                  <tr
                    key={source.id}
                    className="border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark"
                  >
                    <td className="px-6 py-3">
                      <Link
                        to={`/sources/${source.id}`}
                        className="font-medium text-primary dark:text-secondary hover:underline"
                      >
                        {source.name}
                      </Link>
                      {source.description && (
                        <p className="text-xs text-subtle-light dark:text-text-dark/60 truncate max-w-xs">
                          {source.description}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <ModalityBadge modality={source.modality} />
                    </td>
                    <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark font-mono text-xs">
                      {source.source_type}
                      {/* Live monitoring is a setting on a network source, not
                          a separate type, so the type column alone cannot show
                          that this one is agent-fed. */}
                      {isLiveNetworkSource(source) && (
                        <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-sans font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
                          live
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <StatusBadge
                        label={source.is_active ? 'Active' : 'Inactive'}
                        variant={source.is_active ? 'success' : 'neutral'}
                      />
                    </td>
                    <td className="px-6 py-3">
                      {source.has_credential ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                          <span className="material-icons text-sm">key</span>
                          Configured
                        </span>
                      ) : (
                        <Link
                          to={`/sources/${source.id}`}
                          title="Add credential"
                          className="inline-flex items-center gap-1 text-xs font-medium text-warning hover:underline"
                        >
                          <span className="material-icons text-sm">warning</span>
                          Not set
                        </Link>
                      )}
                    </td>
                    <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark text-xs">
                      {formatDate(source.last_scanned_at)}
                    </td>
                    <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark text-xs">
                      {describeScanFrequency(source)}
                    </td>
                    <td className="px-6 py-3">
                      {(() => {
                        const assigned = sourceInventoryMap.get(source.id)
                        return assigned ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 dark:bg-secondary/10 text-primary dark:text-secondary border border-primary/20 dark:border-secondary/20">
                            {assigned.name}
                            <button
                              title={`Remove from ${assigned.name}`}
                              onClick={() => removeFromInventoryMutation.mutate({ inventoryId: assigned.id, sourceId: source.id })}
                              className="ml-0.5 hover:text-error"
                            >
                              <span className="material-icons text-[10px]">close</span>
                            </button>
                          </span>
                        ) : (
                          <button
                            title="Assign to inventory"
                            onClick={() => setAssigningSource(source)}
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-dashed border-border-light dark:border-border-dark text-subtle-light dark:text-subtle-dark hover:border-primary hover:text-primary"
                          >
                            <span className="material-icons text-[12px]">add</span>
                          </button>
                        )
                      })()}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          title="Run Scan"
                          onClick={() => scanMutation.mutate(source.id)}
                          disabled={scanMutation.isPending}
                          className="p-1.5 rounded-md text-subtle-light dark:text-subtle-dark hover:text-primary hover:bg-primary/10"
                        >
                          <span className="material-icons text-base">play_arrow</span>
                        </button>
                        <button
                          title="Edit"
                          onClick={() => setEditingSource(source)}
                          className="p-1.5 rounded-md text-subtle-light dark:text-subtle-dark hover:text-primary hover:bg-primary/10"
                        >
                          <span className="material-icons text-base">edit</span>
                        </button>
                        <Link
                          to={`/sources/${source.id}`}
                          title="View Details"
                          className="p-1.5 rounded-md text-subtle-light dark:text-subtle-dark hover:text-primary hover:bg-primary/10"
                        >
                          <span className="material-icons text-base">open_in_new</span>
                        </Link>
                        <button
                          title="Delete"
                          onClick={() => {
                            if (confirm(`Delete source "${source.name}"?`)) {
                              deleteMutation.mutate(source.id)
                            }
                          }}
                          className="p-1.5 rounded-md text-subtle-light dark:text-subtle-dark hover:text-error hover:bg-error/10"
                        >
                          <span className="material-icons text-base">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
